import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import GUI from 'lil-gui'
import TWEEN, { Tween } from "@tweenjs/tween.js";
import { createNoise3D } from "simplex-noise";

const clock = new THREE.Clock()

export class Viewer3D {

  /**
 * Sizes
 */
  sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
  }

  viewerContainer = null
  fov = 35;
  planeAspectRatio = 4 / 4;
  camera = null
  renderer = null
  composer = null
  gui = null
  scene = null
  canvas = null

  constructor({
    viewerContainer = null,
    canvasElement = null,
    guiVisible = true,
    orbitControlsEnabled = true,
    mouseEventForCamera = false,
    colorBackground = false,
    color1 = false,
    color2 = false,
  }) {

    window.dispatchEvent(new Event('viewerInitStart'))
    /**
     * Base
     */
    // Debug
    this.gui = new GUI({ width: 340 })
    if (!guiVisible) {
      this.gui.hide()
    }
    const debugObject = {}

    // viewerContainer
    this.viewerContainer = viewerContainer ? viewerContainer : document.querySelector('#viewer3DContainer')

    // Canvas
    this.canvas = canvasElement ? canvasElement : document.querySelector('canvas.webgl')

    // Scene
    this.scene = new THREE.Scene()

    // Loaders
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath('./draco/')
    const gltfLoader = new GLTFLoader()
    gltfLoader.setDRACOLoader(dracoLoader)


    /**
* Camera
*/
    // Base camera
    this.camera = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 0.1, 500)
    this.camera.position.set(0, 0, 8 * 2)
    this.scene.add(this.camera)

    // if (mouseEventForCamera) {
    //   window.addEventListener('pointermove', (event) => {

    //     this.camera.lookAt(0, 0, 0)
    //   })
    // }

    // Controls
    const controls = new OrbitControls(this.camera, this.canvas)
    controls.enableDamping = true
    controls.enabled = orbitControlsEnabled

    /**
     * Renderer
     */
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true
    })

    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(this.sizes.pixelRatio)

    debugObject.clearColor = colorBackground ? colorBackground : '#160920'
    this.gui.addColor(debugObject, 'clearColor').onChange(() => { this.renderer.setClearColor(debugObject.clearColor) })
    this.renderer.setClearColor(debugObject.clearColor, 0)
    // this.composer = new EffectComposer(this.renderer)
    // this.composer.addPass(new RenderPass(scene, this.camera))

    // Shader per l'inversione dei colori
    const InvertColorShader = {
      uniforms: {
        "tDiffuse": { value: null }
      },
      vertexShader: `
          varying vec2 vUv;
          void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
      `,
      fragmentShader: `
          uniform sampler2D tDiffuse;
          varying vec2 vUv;
          void main() {
              vec4 color = texture2D(tDiffuse, vUv);
              gl_FragColor = vec4(vec3(1.0) - color.rgb, color.a); // Inverte i colori RGB
          }
      `
    };

    // Aggiunge lo ShaderPass per l'inversione dei colori al composer
    // const invertColorPass = new ShaderPass(InvertColorShader);
    // this.composer.addPass(invertColorPass);

    /**
     * Particles
     */
    const sphereGroup = new THREE.Group()
    // Load models


    const sphereGeo = new THREE.SphereGeometry(5, 200, 100);
    const girdMat = new THREE.MeshPhysicalMaterial({
      color: 0xaaff00 ,
      side: THREE.DoubleSide,
    });

    const grid = new THREE.Mesh(sphereGeo, girdMat);
    const gridEdge = new THREE.EdgesGeometry(sphereGeo);
    const line = new THREE.LineSegments(sphereGeo);
    line.material.color.setHex(0xaaff00);
    line.material.visible = false;

    this.morphWithNoise(sphereGeo, line);

    // grid.translateY(497);
    // line.translateY(497);
    grid.translateY(-5);
    line.translateY(-5);
    grid.scale.set(0.8, 1.0, 1.0);
    line.scale.set(0.8, 1.0, 1.0);
    grid.translateZ(5);
    line.translateZ(5);
    grid.rotateZ(90 * Math.PI / 180);
    line.rotateZ(90 * Math.PI / 180);

    sphereGroup.add(grid);
    // sphereGroup.add(line);

    this.scene.add(sphereGroup)

    const empisphereLight = new THREE.AmbientLight(0xffffff, 1, 100);
    empisphereLight.position.set(0, 0, 0);
    this.scene.add(empisphereLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight1.castShadow = true;

    directionalLight1.shadow.mapSize.width = 1024;
    directionalLight1.shadow.mapSize.height = 1024;

    directionalLight1.shadow.camera.near = 500;
    directionalLight1.shadow.camera.far = 4000;
    directionalLight1.shadow.camera.fov = 30;
    // pointLight.castShadow = true;
    this.scene.add(directionalLight1);
    directionalLight1.position.set(0, 10, 0);

    const directionalLight2 = new THREE.DirectionalLight(0xff0000, 0.8);
    directionalLight2.castShadow = true;

    directionalLight2.shadow.mapSize.width = 1024;
    directionalLight2.shadow.mapSize.height = 1024;

    directionalLight2.shadow.camera.near = 500;
    directionalLight2.shadow.camera.far = 4000;
    directionalLight2.shadow.camera.fov = 30;
    // pointLight.castShadow = true;
    this.scene.add(directionalLight2);
    directionalLight2.position.set(10, 5, 0);

    const directionalLight3 = new THREE.DirectionalLight(0x0000ff, 0.8);
    directionalLight3.castShadow = true;

    directionalLight3.shadow.mapSize.width = 1024;
    directionalLight3.shadow.mapSize.height = 1024;

    directionalLight3.shadow.camera.near = 500;
    directionalLight3.shadow.camera.far = 4000;
    directionalLight3.shadow.camera.fov = 30;
    // pointLight.castShadow = true;
    this.scene.add(directionalLight3);
    directionalLight3.position.set(-10, 5, -10);


    window.dispatchEvent(new Event('viewerInitFinish'))


    window.addEventListener('resize', () => {
      this.handleResize()
    })

    /**
     * Animate
     */
    let val = 0;
    function map_range(value, low1, high1, low2, high2) {
      return low2 + (high2 - low2) * (value - low1) / (high1 - low1);
    }

    let elapsedTime = 0;
    const interval = 5;

    const tick = () => {
      // Update controls
      controls.update()

      // Render normal scene
      this.renderer.render(this.scene, this.camera)

      val += 0.004
      // sphereGroup.rotation.y = map_range(Math.sin(val), -1, 1, -Math.PI / 16, Math.PI / 16)
      // sphereGroup.rotation.x = -map_range(Math.sin(val) + Math.cos(val), -2, 2, -Math.PI / 16, Math.PI / 16) * 1.2
      // sphereGroup.rotation.z = map_range(Math.sin(val) + Math.cos(val), -2, 2, -Math.PI / 16, Math.PI / 16) * 0.1

      // Call tick again on the next frame
      window.requestAnimationFrame(tick)

      const delta = clock.getDelta();
      elapsedTime += delta;
      TWEEN.update();
    }

    tick()
  }

  handleResize = () => {
    // Update sizes
    // this.sizes.width = window.innerWidth
    // this.sizes.height = window.innerHeight
    //get the canvas size
    this.sizes.width = this.viewerContainer ?  this.viewerContainer.offsetWidth : window.innerWidth;
    this.sizes.height = this.viewerContainer ?  this.viewerContainer.offsetHeight : window.innerWidth;
    this.sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Materials
    if (this.particles) {
      this.particles.material.uniforms.uResolution.value.set(this.sizes.width * this.sizes.pixelRatio, this.sizes.height * this.sizes.pixelRatio)
    }

    // Update camera
    this.camera.aspect = this.sizes.width / this.sizes.height

    if (this.camera.aspect < this.planeAspectRatio) {
      // window too large
      const cameraHeight = Math.tan(THREE.MathUtils.degToRad(this.fov / 2));
      const ratio = this.camera.aspect / this.planeAspectRatio;
      const newCameraHeight = cameraHeight / ratio;
      this.camera.fov = THREE.MathUtils.radToDeg(Math.atan(newCameraHeight)) * 2;
    } else {
      // window too narrow
      this.camera.fov = this.fov;
    }

    this.camera.updateProjectionMatrix()

    // Update renderer
    this.renderer.setSize(this.sizes.width, this.sizes.height)
    this.renderer.setPixelRatio(this.sizes.pixelRatio)
    // this.composer.setSize(this.sizes.width, this.sizes.height)
  }

  morphWithNoise(geometry, objLine) {
    const clock = new THREE.Clock();
    const noise = new createNoise3D();
    const oldVectors = [];
    const noiseParams = {
      vectorMultyX: 0.4,
      vectorMultyY: 0.2,
      vectorMultyZ: 0.06,
      timeMultyX: 0.06,
      timeMultyY: 0.04,
      timeMultyZ: 0.00,
      perlinMulty: 0.15,
      perlinAdd: 1.122,
      zScale: 0.2
    };

    const noiseFolder = this.gui?.addFolder("Noise-fun");
    noiseFolder?.add(noiseParams, "vectorMultyX").max(4).min(0).step(0.001).name("vectorMultyX");
    noiseFolder?.add(noiseParams, "vectorMultyY").max(4).min(0).step(0.001).name("vectorMultyY");
    noiseFolder?.add(noiseParams, "vectorMultyZ").max(4).min(0).step(0.001).name("vectorMultyZ");
    noiseFolder?.add(noiseParams, "timeMultyX").max(4).min(0).step(0.001).name("timeMultyX");
    noiseFolder?.add(noiseParams, "timeMultyY").max(4).min(0).step(0.001).name("timeMultyY");
    noiseFolder?.add(noiseParams, "timeMultyZ").max(4).min(0).step(0.001).name("timeMultyZ");
    noiseFolder?.add(noiseParams, "perlinMulty").max(4).min(0).step(0.001).name("perlinMulty");
    noiseFolder?.add(noiseParams, "perlinAdd").max(4).min(0).step(0.001).name("perlinAdd");
    noiseFolder?.add(noiseParams, "zScale").max(4).min(0).step(0.001).name("zScale");

    for (let index = 0; index < geometry.attributes.position.count; index++) {

      oldVectors.push(new THREE.Vector3(geometry.attributes.position.getX(index),
        geometry.attributes.position.getY(index),
        geometry.attributes.position.getZ(index)
      ));
    }

    const morphTween = new Tween().onUpdate(() => {
      const time = clock.getElapsedTime();
      for (let index = 0; index < geometry.attributes.position.count; index++) {

        const vector = new THREE.Vector3(oldVectors[index].x,
          oldVectors[index].y,
          oldVectors[index].z
        );

        const perlin = noise(
          (vector.x * noiseParams.vectorMultyX) + (time * noiseParams.timeMultyX),
          (vector.y * noiseParams.vectorMultyY) + (time * noiseParams.timeMultyY),
          (vector.z * noiseParams.vectorMultyZ) + (time * noiseParams.timeMultyZ)
        );

        const ratio = ((perlin * noiseParams.perlinMulty) + noiseParams.perlinAdd);
        vector.multiplyScalar(ratio);
        geometry.attributes.position.setX(index, vector.x);
        geometry.attributes.position.setY(index, vector.y);
        geometry.attributes.position.setZ(index, vector.z + (Math.sin(perlin) * noiseParams.zScale));
      }
      geometry.computeVertexNormals();
      geometry.attributes.position.needsUpdate = true;
      if (objLine) {
        objLine.geometry.attributes.position.needsUpdate = true;
      }
    }).repeat(Infinity);

    morphTween.start();
  }
}
